import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminNavigation from '../../components/AdminNavigation';
import NavigationBreadcrumbs from '../../components/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';
import ServiceCard from './components/ServiceCard';
import ServiceModal from './components/ServiceModal';
import AnalyticsModal from './components/AnalyticsModal';

const AdminProductsManagement = () => {
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [services, setServices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((filteredCount || 0) / PAGE_SIZE));
  }, [filteredCount]);

  const mapServiceRow = (row) => {
    return {
      id: row.id,
      name: row.name,
      description: row.description,
      basePrice: row.base_price,
      promotionalPrice: row.promotional_price,
      turnaroundTime: row.turnaround_time,
      features: row.features,
      isActive: row.active,
      lastModified: row.created_at,
      promotionStartDate: row.promotion_start_date,
      promotionEndDate: row.promotion_end_date,
    };
  };

  const fetchServices = useCallback(async () => {
    setIsFetching(true);
    setFetchError('');

    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('services')
        .select(
          'id,name,description,base_price,promotional_price,active,created_at,promotion_start_date,promotion_end_date,turnaround_time,features',
          { count: 'exact' }
        )
        .order('created_at', { ascending: false })
        .range(from, to);

      if (filterStatus === 'active') {
        query = query.eq('active', true);
      }

      if (filterStatus === 'inactive') {
        query = query.eq('active', false);
      }

      if (searchQuery) {
        const q = searchQuery.trim();
        if (q) {
          query = query.or(`name.ilike.%${q}%,description.ilike.%${q}%`);
        }
      }

      const { data, error, count } = await query;
      if (error) throw error;

      const mapped = (data || []).map(mapServiceRow);
      setServices(mapped);
      setFilteredCount(count || 0);

      const [{ count: allCount }, { count: aCount }, { count: iCount }] = await Promise.all([
        supabase.from('services').select('id', { count: 'exact', head: true }),
        supabase.from('services').select('id', { count: 'exact', head: true }).eq('active', true),
        supabase.from('services').select('id', { count: 'exact', head: true }).eq('active', false),
      ]);

      setTotalCount(allCount || 0);
      setActiveCount(aCount || 0);
      setInactiveCount(iCount || 0);
    } catch (e) {
      setServices([]);
      setFilteredCount(0);
      setFetchError(e?.message || '서비스 목록을 불러오지 못했습니다.');
    } finally {
      setIsFetching(false);
    }
  }, [filterStatus, page, searchQuery]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleAddService = () => {
    setSelectedService(null);
    setIsServiceModalOpen(true);
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setIsServiceModalOpen(true);
  };

  const handleSaveService = async (serviceData) => {
    const payload = {
      name: serviceData?.name,
      description: serviceData?.description,
      base_price: serviceData?.basePrice,
      promotional_price: serviceData?.promotionalPrice,
      active: !!serviceData?.isActive,
      promotion_start_date: serviceData?.promotionStartDate || null,
      promotion_end_date: serviceData?.promotionEndDate || null,
      turnaround_time: serviceData?.turnaroundTime || null,
      features: serviceData?.features || null,
    };

    if (selectedService?.id) {
      await supabase.from('services').update(payload).eq('id', selectedService.id);
    } else {
      await supabase.from('services').insert(payload);
    }

    fetchServices();
  };

  const handleToggleStatus = async (serviceId) => {
    const target = services.find((s) => s.id === serviceId);
    if (!target) return;

    await supabase.from('services').update({ active: !target.isActive }).eq('id', serviceId);
    fetchServices();
  };

  const handleDuplicateService = async (service) => {
    const payload = {
      name: `${service?.name} (복사본)`,
      description: service?.description,
      base_price: service?.basePrice,
      promotional_price: service?.promotionalPrice,
      active: !!service?.isActive,
      promotion_start_date: service?.promotionStartDate || null,
      promotion_end_date: service?.promotionEndDate || null,
      turnaround_time: service?.turnaroundTime || null,
      features: service?.features || null,
    };

    await supabase.from('services').insert(payload);
    fetchServices();
  };

  const handleDeleteService = async (serviceId) => {
    if (!serviceId) return;
    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;

    await supabase.from('services').delete().eq('id', serviceId);
    fetchServices();
  };

  const handleViewAnalytics = (service) => {
    setSelectedService(service);
    setIsAnalyticsModalOpen(true);
  };

  const handleStatusFilterChange = (next) => {
    setSelectedService(null);
    setPage(1);
    setFilterStatus(next);
  };

  const handleSearchChange = (next) => {
    setSelectedService(null);
    setPage(1);
    setSearchQuery(next);
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <NavigationBreadcrumbs />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-lg bg-card p-6 shadow-card">
          <div className="mb-6 flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-foreground">상품 관리</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                서비스 유형과 가격을 관리하고 고객에게 제공되는 옵션을 설정합니다
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddService}
              >
                새 서비스 추가
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">전체 서비스</p>
                <Icon name="Package" size={20} className="text-accent" />
              </div>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>

            <div className="rounded-lg bg-success/10 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">활성 서비스</p>
                <Icon name="CheckCircle" size={20} className="text-success" />
              </div>
              <p className="text-2xl font-bold text-foreground">{activeCount}</p>
            </div>

            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">비활성 서비스</p>
                <Icon name="XCircle" size={20} className="text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-foreground">{inactiveCount}</p>
            </div>
          </div>
        </div>

        <div className="mb-6 rounded-lg bg-card p-6 shadow-card">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="relative flex-1 sm:max-w-md">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="서비스 이름 또는 설명으로 검색..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e?.target?.value)}
                className="w-full rounded-md border border-input bg-muted py-2 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleStatusFilterChange('all')}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all duration-150 ease-out ${
                  filterStatus === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                전체
              </button>
              <button
                onClick={() => handleStatusFilterChange('active')}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all duration-150 ease-out ${
                  filterStatus === 'active' ?'bg-success text-success-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                활성
              </button>
              <button
                onClick={() => handleStatusFilterChange('inactive')}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all duration-150 ease-out ${
                  filterStatus === 'inactive' ?'bg-muted text-foreground ring-2 ring-border' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                비활성
              </button>
            </div>
          </div>
        </div>

        {isFetching ? (
          <div className="rounded-lg bg-card p-12 text-center shadow-card">
            <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-sm text-muted-foreground">서비스 목록을 불러오는 중...</p>
          </div>
        ) : fetchError ? (
          <div className="rounded-lg bg-card p-6 shadow-card">
            <div className="rounded-lg bg-error/10 p-4">
              <p className="text-sm text-error">{fetchError}</p>
            </div>
          </div>
        ) : services?.length === 0 ? (
          <div className="rounded-lg bg-card p-12 text-center shadow-card">
            <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-bold text-foreground">서비스가 없습니다</h3>
            <p className="mb-6 text-sm text-muted-foreground">
              {searchQuery
                ? '검색 조건에 맞는 서비스를 찾을 수 없습니다' :'새로운 서비스를 추가하여 시작하세요'}
            </p>
            {!searchQuery && (
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddService}
              >
                첫 서비스 추가하기
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {services?.map((service) => (
                <ServiceCard
                  key={service?.id}
                  service={service}
                  onEdit={handleEditService}
                  onToggleStatus={handleToggleStatus}
                  onDuplicate={handleDuplicateService}
                  onDelete={handleDeleteService}
                  onViewAnalytics={handleViewAnalytics}
                />
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 rounded-lg bg-card p-4 shadow-card sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                총 <span className="font-medium text-foreground">{filteredCount}</span>개
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <span className="text-sm text-muted-foreground">
                  페이지 <span className="font-medium text-foreground">{page}</span> / {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    이전
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    다음
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      <ServiceModal
        isOpen={isServiceModalOpen}
        onClose={() => {
          setIsServiceModalOpen(false);
          setSelectedService(null);
        }}
        service={selectedService}
        onSave={handleSaveService}
      />
      <AnalyticsModal
        isOpen={isAnalyticsModalOpen}
        onClose={() => {
          setIsAnalyticsModalOpen(false);
          setSelectedService(null);
        }}
        service={selectedService}
      />
    </div>
  );
};

export default AdminProductsManagement;